// ./src/utils/api/GuestDrawService.ts
//
// Prize-draw rounds (#30) — a host runs random name draws over an event's guests
// and keeps the auditable result. Mirrors the eventsmagic-api GuestDraw SDL. Each
// round has tiered per-rank prizes and, once drawn, ranked winners (snapshotted
// name + prize). `run` (guestDrawRun) does the random pick; `where.id` accepts one
// id or many (a bulk update may only change status, server-enforced).
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';
import { Pagination } from './commonApiTypes';

const WINNER_FIELDS = `
  id
  guestDrawId
  eventGuestId
  personId
  displayName
  prize
  orderNo
  createdAt
`;

const FIELDS = `
  id
  eventId
  name
  description
  winnersCount
  prizes { label description }
  audienceFilter
  audienceSize
  excludePreviousWinners
  drawnAt
  drawAlgorithm
  drawCount
  lastDrawnByUserId
  lastDrawnByEventGuestId
  lastDrawnByLabel
  isPublished
  pendingMessage
  announcedAt
  notes
  orderNo
  status
  rights
  winners { ${WINNER_FIELDS} }
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query GuestDrawList($filter: GuestDrawFilter, $params: PaginationAndSorting) {
    guestDrawList(filter: $filter, params: $params) {
      code
      total
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query GuestDrawGet($id: ID) {
    guestDrawGet(id: $id) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation GuestDrawCreate($params: GuestDrawInput) {
    guestDrawCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation GuestDrawUpdate($where: GuestDrawWhereInput, $params: GuestDrawInput) {
    guestDrawUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation GuestDrawRemove($where: GuestDrawWhereInput) {
    guestDrawRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const RUN_MUTATION = gql`
  mutation GuestDrawRun($params: GuestDrawRunInput) {
    guestDrawRun(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

export interface GuestDrawPrize {
  label?: string | null;
  description?: string | null;
}

export interface GuestDrawWinner {
  id?: string;
  guestDrawId?: string | null;
  eventGuestId?: string | null;
  personId?: string | null;
  displayName?: string | null;
  prize?: string | null;
  orderNo?: number | null;
  createdAt?: string | null;
}

export interface GuestDraw {
  id?: string;
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  winnersCount?: number | null;
  prizes?: GuestDrawPrize[] | null;
  /** Opaque snapshot of the audience filter used (for display / redraw). */
  audienceFilter?: Record<string, unknown> | null;
  audienceSize?: number | null;
  excludePreviousWinners?: boolean | null;
  drawnAt?: string | null;
  /** RNG/algorithm id used for the last draw (audit). */
  drawAlgorithm?: string | null;
  /** How many times this round has been drawn (increments on redraw). */
  drawCount?: number | null;
  /** The account user who ran the last draw (audit; null for password crew). */
  lastDrawnByUserId?: string | null;
  /** The non-account (password) crew member who ran the last draw (audit). */
  lastDrawnByEventGuestId?: string | null;
  /** Snapshotted name of who ran the last draw (account user OR crew). */
  lastDrawnByLabel?: string | null;
  isPublished?: boolean | null;
  pendingMessage?: string | null;
  announcedAt?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
  rights?: string[] | null;
  winners?: GuestDrawWinner[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface GuestDrawInput {
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  winnersCount?: number | null;
  prizes?: GuestDrawPrize[] | null;
  audienceFilter?: Record<string, unknown> | null;
  excludePreviousWinners?: boolean | null;
  isPublished?: boolean | null;
  pendingMessage?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface GuestDrawRunInput {
  id: string;
  /** The audience to draw from (guest ids); omit to draw from all active attendees. */
  eventGuestIds?: string[] | null;
}

export class GuestDrawService {
  /** One page of an event's rounds, by display order, with `total` (ROADMAP #112 — it read every round at once). */
  static async list(eventId: string, statuses: number[] | undefined, pagination: Pagination): Promise<OpResult> {
    return ApiService.executeQuery(
      LIST_QUERY,
      { filter: { eventId: [eventId], status: statuses }, params: { pagination } },
      { fetchPolicy: "network-only" }
    );
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id }, { fetchPolicy: "network-only" });
  }

  static async create(params: GuestDrawInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: GuestDrawInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  /** Draw (or redraw) a round's winners. */
  static async run(params: GuestDrawRunInput): Promise<OpResult> {
    return ApiService.executeMutation(RUN_MUTATION, { params });
  }
}
